import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import {
  createSponsorImpression,
  createSponsorClick,
  trackAdPixel,
} from "core/utils/tracker";
import ButtonBase from "@mui/material/ButtonBase";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { nanoid } from "nanoid";
import {
  trackBrandingEvent,
  BRANDING_IMPRESSION,
  BRANDING_CLICK,
} from "core/utils/adAnalytics";
import { CORNER_SCALE } from "core/components/PlayerInView/PlayerInView";

/**
 * Инстрим-брендинг: рамка-креатив на весь плеер под видео. Видео ужимается до
 * 80% от нижнего края (PlayerInView), сверху и по бокам открывается рамка.
 *
 * Картинка рамки сначала полностью загружается, и только потом засчитывается
 * показ и начинается поджатие: раньше фон грузился во время анимации, и вокруг
 * ужимающегося видео была видна чернота.
 */
const StyledButtonBase = styled(ButtonBase)(() => ({
  display: "block",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  height: "100%",
  width: "100%",
  zIndex: 1,
  backgroundPosition: "top center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
}));

// Ссылка рамки поверх слоёв плеера: шапка с названием лежит над верхней полосой
// рамки, мигала при движении мыши, давала текстовый курсор и забирала клики.
// Вырез: видео (CORNER_SCALE от нижнего края по центру) и нижняя полоса под
// панель управления, чтобы не перекрывать её кнопки.
const FOOTER_RESERVED_PX = 72;
const FRAME_LINK_Z_INDEX = 3;

const FrameLink = styled(ButtonBase)({
  display: "block",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: "100%",
  height: "100%",
  zIndex: FRAME_LINK_Z_INDEX,
  cursor: "pointer",
  background: "transparent",
});

const pct = (n) => `${Math.round(n * 1000) / 10}%`;

/**
 * clip-path рамки: весь плеер минус видео и нижняя полоса панели управления
 */
export function frameClipPath(
  scale = CORNER_SCALE,
  footerPx = FOOTER_RESERVED_PX
) {
  const side = pct((1 - scale) / 2);
  const right = pct(1 - (1 - scale) / 2);
  const top = pct(1 - scale);
  const bottom = `calc(100% - ${footerPx}px)`;

  return (
    `polygon(evenodd, 0% 0%, 100% 0%, 100% ${bottom}, 0% ${bottom}, 0% 0%, ` +
    `${side} ${top}, ${right} ${top}, ${right} ${bottom}, ${side} ${bottom}, ${side} ${top})`
  );
}

const SHOW_BANNER_TIMEOUT = 15000; // Через сколько мс закрывать баннер
const ANIMATION_DURATION = 1000; // Длительность анимации (как у поджатия в PlayerInView)

const IMPRESSION = "IMPRESSION";
const DEFAULT = "DEFAULT";
const MEDIUM = "MEDIUM";
const HIGH = "HIGH";
const MAXRES = "MAXRES";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

function findImageUrl(formats, { smUp, mdUp, lgUp }) {
  const indexByFormat = formats.reduce((all, item) => {
    all[item.format] = item.url;
    return all;
  }, {});

  if (lgUp && indexByFormat[MAXRES]) return indexByFormat[MAXRES];
  if (mdUp && indexByFormat[HIGH]) return indexByFormat[HIGH];
  if (smUp && indexByFormat[MEDIUM]) return indexByFormat[MEDIUM];
  if (indexByFormat[DEFAULT]) return indexByFormat[DEFAULT];

  return indexByFormat[MAXRES];
}

/**
 * Рамка под пропорции плеера: в вертикальном плеере (выше, чем шире) вертикальный
 * вариант, если он загружен. Горизонтальная рамка там сильно обрезается.
 */
export function pickBrandingThumbnails({
  thumbnails,
  verticalThumbnails,
  width,
  height,
}) {
  if (width > 0 && height > width && verticalThumbnails?.length) {
    return verticalThumbnails;
  }

  return thumbnails || [];
}

export default function SponsorCornerBanner({
  onStart,
  onFinish,
  setCornerBannerHeight,
  cornerBanner,
}) {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const bannerRef = useRef();
  const timersRef = useRef([]);
  const startedRef = useRef(false);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [loadedSrc, setLoadedSrc] = useState(null);
  const [active, setActive] = useState(false);

  const {
    token,
    thumbnails,
    verticalThumbnails,
    link,
    isClickable,
    clickPixel,
    impressionPixel,
  } = cornerBanner;

  // Размер плеера (рамка на весь плеер): выбираем вариант до загрузки картинки
  useIsomorphicLayoutEffect(() => {
    if (bannerRef.current) {
      setSize({
        width: bannerRef.current.offsetWidth,
        height: bannerRef.current.offsetHeight,
      });
    }
  }, []);

  const src = useMemo(() => {
    if (!size.width) return null;

    const selected = pickBrandingThumbnails({
      thumbnails,
      verticalThumbnails,
      ...size,
    });

    return selected.length
      ? findImageUrl(selected, { smUp, mdUp, lgUp })
      : null;
  }, [size, thumbnails, verticalThumbnails, smUp, mdUp, lgUp]);

  // Предзагрузка: показ стартует только с готовой картинкой
  useEffect(() => {
    if (!src || startedRef.current) return undefined;

    let cancelled = false;
    const image = new Image();

    image.onload = () => {
      if (cancelled || startedRef.current) return;
      startedRef.current = true;
      setLoadedSrc(src);

      createSponsorImpression(token, "In-Stream Branding", IMPRESSION);

      trackBrandingEvent(BRANDING_IMPRESSION, { format: "frame" });

      if (impressionPixel) {
        trackAdPixel(impressionPixel);
      }

      onStart();

      // Рамка уже нарисована под видео: поджатие открывает готовую картинку
      window.requestAnimationFrame(() => {
        setCornerBannerHeight(bannerRef.current?.offsetHeight || 1);
        setActive(true);
      });

      timersRef.current.push(
        setTimeout(() => {
          setActive(false);
          setCornerBannerHeight(null);
        }, SHOW_BANNER_TIMEOUT - ANIMATION_DURATION),
        setTimeout(() => {
          setCornerBannerHeight(null);
          onFinish();
        }, SHOW_BANNER_TIMEOUT)
      );
    };

    image.onerror = () => {
      if (cancelled || startedRef.current) return;
      startedRef.current = true;
      onFinish();
    };

    image.src = src;

    return () => {
      cancelled = true;
    };
  }, [src]);

  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      setCornerBannerHeight(null);
    },
    [setCornerBannerHeight]
  );

  // Клик обычной ссылкой: window.open WebView приложений (iOS) игнорирует
  const clickId = useMemo(() => nanoid(), []);
  const href =
    isClickable && link ? link.replace("{clickId}", clickId) : undefined;

  const onClick = (event) => {
    if (!href) {
      event.preventDefault();
      return;
    }

    if (clickPixel) {
      trackAdPixel(clickPixel);
    }

    trackBrandingEvent(BRANDING_CLICK, { format: "frame" });

    createSponsorClick(token, clickId);
  };

  const linkProps = href
    ? { component: "a", href, target: "_blank", rel: "noopener noreferrer" }
    : { component: "div" };

  return (
    <>
      {/* Картинка рамки под видео (поджатие её открывает) */}
      <StyledButtonBase
        ref={bannerRef}
        component="div"
        tabIndex={-1}
        style={
          loadedSrc ? { backgroundImage: `url('${loadedSrc}')` } : undefined
        }
        onClick={onClick}
      />
      {/* Кликабельная область рамки поверх шапки плеера, с вырезом под видео */}
      {active && (
        <FrameLink
          aria-label="advertising"
          style={{ clipPath: frameClipPath() }}
          onClick={onClick}
          {...linkProps}
        />
      )}
    </>
  );
}

SponsorCornerBanner.propTypes = {
  onStart: PropTypes.func,
  onFinish: PropTypes.func,
  cornerBanner: PropTypes.shape({
    id: PropTypes.string.isRequired,
    thumbnails: PropTypes.arrayOf(
      PropTypes.shape({
        format: PropTypes.oneOf([DEFAULT, MEDIUM, HIGH, MAXRES]).isRequired,
        url: PropTypes.string.isRequired,
      })
    ).isRequired,
    verticalThumbnails: PropTypes.arrayOf(
      PropTypes.shape({
        format: PropTypes.oneOf([DEFAULT, MEDIUM, HIGH, MAXRES]).isRequired,
        url: PropTypes.string.isRequired,
      })
    ),
    link: PropTypes.string,
    token: PropTypes.string.isRequired,
    isClickable: PropTypes.bool,
    clickPixel: PropTypes.string,
    impressionPixel: PropTypes.string,
  }),
  setCornerBannerHeight: PropTypes.func,
};

SponsorCornerBanner.defaultProps = {
  onStart() {},
  onFinish() {},
  setCornerBannerHeight() {},
};
